import { location, onMount, reactive } from 'cdui-js';
import i18n from '../../../../i18n';
import { DiFiSkeleton, DiFiSkeletonParagraph } from '../../../../components/DiFi/DiFiSkeleton';
import { showActionSheet } from '../../components/ActionSheet';
import { ActionSheetCheckIcon } from '../../components/ActionSheet/CheckIcon';
import { IconBank, IconChevronRight, IconCopy } from './icons';
import { DepositShell } from './Shell';
import {
  brokerAccountNum,
  brokerBankCode,
  brokerBranchCode,
  copyText,
  dash,
  depositUrl,
  getMarketBankCache,
  isChequePath,
  localizedBankName,
  readDepositQuery,
} from './shared';
import { ensureDepositBanks } from './preload';
import './deposit.css';

const payeeSkeleton = () => (
  <div class="payee flex-column gap-12px">
    <DiFiSkeletonParagraph rowWidth="40%" style={{ height: '20px' }} />
    <DiFiSkeletonParagraph rowWidth="80%" style={{ height: '14px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '72px', 'margin-top': '8px', 'border-radius': '12px' }} />
    <DiFiSkeletonParagraph rowWidth="100%" style={{ height: '160px', 'border-radius': '12px' }} />
  </div>
);

export const PayeePage = () => {
  const state = reactive({
    banks: [] as any[],
    index: 0,
    loading: true,
  });

  onMount(() => {
    ensureDepositBanks(readDepositQuery().currency)
      .then((pack) => {
        state.banks = pack.brokerBanks;
        state.index = 0;
      })
      .finally(() => {
        state.loading = false;
      });
  });

  const selected = () => state.banks[state.index];
  const displayName = () => localizedBankName(selected(), getMarketBankCache());

  const openPicker = () => {
    if (state.banks.length <= 1) return;
    const sheet = showActionSheet({
      title: i18n.H5Deposit.SelectPayeeBank,
      content: () => (
        <div>
          {state.banks.map((row, index) => {
            const active = index === state.index;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  state.index = index;
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label">{localizedBankName(row, getMarketBankCache())}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const next = () => {
    const row = selected();
    const path = isChequePath() ? '/h5/deposits/cheque/step1' : '/h5/deposits/bank/step2';
    location.routeTo(
      depositUrl(path, row
        ? {
            payeeBankAccountNum: brokerAccountNum(row),
            payeeBankCode: brokerBankCode(row),
            payeeBankName: String(row.bankName || ''),
          }
        : {}),
    );
  };

  const rows = () => {
    const row = selected();
    return [
      { label: i18n.H5Deposit.LabelBankName, value: row ? displayName() : '' },
      { label: i18n.H5Deposit.LabelBankCode, value: row ? brokerBranchCode(row) : '' },
      { label: i18n.H5Deposit.LabelAccountName, value: row?.accountName || '' },
      { label: i18n.H5Deposit.LabelAccountNo, value: row ? brokerAccountNum(row) : '' },
    ];
  };

  return (
    <DepositShell
      footer={
        state.loading ? null : (
          <div class="footer">
            <button type="button" class="btn-primary" onclick={next}>
              {i18n.H5Deposit.SubmitProofCta}
            </button>
            <div
              class="contact"
              onclick={() => {
                if (isChequePath()) return;
                location.routeTo('/help-center');
              }}
            >
              {i18n.H5Deposit.ContactCs}
            </div>
          </div>
        )
      }
    >
      <DiFiSkeleton loading={state.loading} template={payeeSkeleton()}>
        <div class="payee">
          <div>
            <div class="payee-title">{i18n.H5Deposit.PayeeTitle}</div>
            <div class="payee-desc">{i18n.H5Deposit.PayeeDesc}</div>
          </div>

          <div class="bank-card" onclick={openPicker}>
            <IconBank />
            <div style={{ flex: 1 }}>
              <div class="bank-name">{selected() ? displayName() : '-'}</div>
              <div class="bank-no">{dash(selected() ? brokerAccountNum(selected()) : '')}</div>
            </div>
            {state.banks.length > 1 ? <IconChevronRight /> : null}
          </div>

          <div class="detail-card">
            {rows().map((item) => (
              <div class="detail-row">
                <div class="detail-label">{item.label}</div>
                <div class="detail-value">
                  <span>{dash(item.value)}</span>
                  <button type="button" class="copy-btn" onclick={() => copyText(item.value)}>
                    <IconCopy />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DiFiSkeleton>
    </DepositShell>
  );
};

export const DepositBankStep1 = PayeePage;
export const DepositChequeStep0 = PayeePage;
